/**
 * Ingresar unidades repartidas entre los colores de un modelo.
 *
 * POR QUE ES UN HOOK Y NO UNA FUNCION DE LA PANTALLA
 * --------------------------------------------------
 * Lo necesitan dos: /inventario, que tiene el ingreso y el conteo, y Catalogo, que
 * tiene el conteo de cada fila. Cuando esto vivia dentro de /inventario, Catalogo
 * no lo tenia — y Catalogo es donde se ve el cartel de «datos sin completar»
 * que este reparto existe para apagar.
 *
 * QUE HACE
 * --------
 * La fila sin color es la lista de precios del sellado: dice cuanto sale y en
 * que colores viene, y se queda en cero. Las unidades van a la ficha de SU
 * color, que se busca entre las que ya hay y, si no esta, se crea copiando los
 * numeros del modelo — el precio y el costo son los mismos; lo unico que cambia
 * es cual equipo es.
 *
 * BUSCAR Y CREAR LOS HACE `iphones/entrada.ts`. Aca vivio una segunda version
 * de las dos cosas: armaba el nombre con el color adentro y despues buscaba la
 * ficha comparando ese texto, que desde la migracion 0075 no encuentra nunca la
 * que esta y crea una segunda.
 */
import { useCrearProducto, useIngresoStock } from "@/lib/queries";
import type { IngresoStock, Producto } from "@/lib/tipos";

import { entradaDeColor, fichaDeLaEntrada, fichaNueva } from "@/components/iphones/entrada";

import { nombreCompleto } from "./nombre";
import type { UnidadesDeColor } from "./RepartoPorColor";

export interface ResultadoDelReparto {
  /** Una linea por color: "2 IPHONE 18 PRO 256 GB · Burgundy". */
  hechos: string[];
}

export interface IngresoRepartido {
  /**
   * `productos` TIENE QUE SER LA LISTA PLANA, la que trae las fichas de color.
   *
   * Las pantallas que agrupan por modelo tienen a mano una lista donde las
   * unidades no estan: cuelgan de su modelo, adentro del grupo. Pasar esa es
   * pedir que no encuentre la ficha que ya existe y cree una segunda, que es
   * exactamente el bug que este modulo vino a sacar. En las dos pantallas la
   * lista correcta es la que devuelve el servidor, antes de agrupar.
   */
  ingresar: (
    datos: IngresoStock,
    reparto: ReadonlyArray<UnidadesDeColor>,
    productos: ReadonlyArray<Producto>,
  ) => Promise<ResultadoDelReparto>;
  guardando: boolean;
}

export function useIngresoRepartido(): IngresoRepartido {
  const crear = useCrearProducto();
  const ingresar = useIngresoStock();

  /*
   * DE A UN COLOR POR VEZ Y NO EN PARALELO. Dos colores que todavia no existen
   * se crean con dos altas, y dos altas simultaneas del mismo modelo son la
   * forma mas facil de volver a tener dos fichas del mismo equipo — que es el
   * bug que esto acaba de dejar de tener.
   *
   * Si falla a la mitad quedan unos colores entrados y otros no. Es visible en
   * la lista, que muestra el stock de cada uno, y se completa reintentando por
   * los que faltan: el ingreso busca la ficha antes de crearla, asi que repetir
   * no duplica.
   */
  async function aplicar(
    datos: IngresoStock,
    reparto: ReadonlyArray<UnidadesDeColor>,
    productos: ReadonlyArray<Producto>,
  ): Promise<ResultadoDelReparto> {
    const modelo = productos.find((p) => p.id === datos.producto_id);
    if (!modelo) return { hechos: [] };

    const hechos: string[] = [];
    for (const { color, cantidad } of reparto) {
      const equipo = entradaDeColor(modelo, color);
      const existente = fichaDeLaEntrada(productos, equipo);
      const destino = existente ?? (await crear.mutateAsync(fichaNueva(equipo, modelo)));
      await ingresar.mutateAsync({ ...datos, cantidad, producto_id: destino.id });
      hechos.push(`${cantidad} ${nombreCompleto(destino)}`);
    }
    return { hechos };
  }

  return { ingresar: aplicar, guardando: crear.isPending || ingresar.isPending };
}
